import { QRCodeSVG } from "qrcode.react";
import { Button } from "../components/ui/button";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  Globe,
  Facebook,
  Instagram,
  UserPlus,
  Share2,
  MapPin
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_citizen-restart-fund/artifacts/35d9mfhq_6zh9nv37_transparent%20background.png";

const VCardPage = () => {
  const cardData = {
    name: "Diane Slappy",
    title: "BSN, RN",
    organization: "New Leaf Independent Housing",
    tagline: "Rooted, Connected & Growing in Purpose",
    phone: "(912) 698-1099",
    phoneClean: "9126981099",
    email: "info@newleaf-housing.com",
    website: "newleafhousing.info",
    facebook: "NewLeafIndependentHousing",
    instagram: "newleaf_independent_housing"
  };

  // Generate vCard string
  const generateVCard = () => {
    return `BEGIN:VCARD
VERSION:3.0
FN:${cardData.name}
ORG:${cardData.organization}
TITLE:${cardData.title}
TEL;TYPE=WORK,VOICE:${cardData.phone}
EMAIL:${cardData.email}
URL:https://${cardData.website}
NOTE:${cardData.tagline}
END:VCARD`;
  };

  // Download vCard file
  const downloadVCard = () => {
    const vCardContent = generateVCard();
    const blob = new Blob([vCardContent], { type: 'text/vcard' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'diane-slappy-newleaf.vcf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success("Contact card downloaded!");
  };

  // Share functionality
  const shareCard = async () => {
    const shareData = {
      title: `${cardData.name} - ${cardData.organization}`,
      text: `Contact ${cardData.name} at ${cardData.organization}. Phone: ${cardData.phone}, Email: ${cardData.email}`,
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        toast.success("Shared successfully!");
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyLink();
        }
      }
    } else {
      copyLink();
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    } catch (err) {
      toast.error("Unable to copy link");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F9F8F5] to-[#EBEAE4] flex items-center justify-center p-4">
      <Toaster position="top-center" richColors />
      
      <div className="w-full max-w-md">
        {/* Card Container */}
        <div 
          className="bg-white rounded-3xl shadow-2xl overflow-hidden"
          data-testid="vcard-container"
        >
          {/* Header with Logo */}
          <div className="bg-[#265C39] p-6 text-center">
            <img 
              src={LOGO_URL} 
              alt="New Leaf Housing Logo" 
              className="h-16 w-auto mx-auto mb-3"
              data-testid="vcard-logo"
            />
            <h1 className="text-white text-xl font-bold font-['Manrope']">
              {cardData.organization}
            </h1>
            <p className="text-[#8CA891] text-sm italic mt-1">
              {cardData.tagline}
            </p>
          </div>

          {/* Profile Section */}
          <div className="px-6 py-5 text-center border-b border-[#EBEAE4]">
            <h2 className="text-2xl font-bold text-[#1A2E20] font-['Manrope']">
              {cardData.name}
            </h2>
            <p className="text-[#C57E65] font-semibold mt-1">
              {cardData.title}
            </p>
          </div>

          {/* QR Code Section */}
          <div className="p-6 flex flex-col items-center bg-[#F9F8F5]">
            <div 
              className="bg-white p-4 rounded-2xl shadow-md"
              data-testid="vcard-qr-code"
            >
              <QRCodeSVG
                value={generateVCard()}
                size={200}
                level="H"
                includeMargin={true}
                fgColor="#265C39"
                bgColor="#FFFFFF"
                imageSettings={{
                  src: LOGO_URL,
                  x: undefined,
                  y: undefined,
                  height: 40,
                  width: 40,
                  excavate: true,
                }}
              />
            </div>
            <p className="text-[#6C7D71] text-sm mt-3">Scan to save contact</p>
          </div>

          {/* Contact Info */}
          <div className="px-6 py-4 space-y-3">
            {/* Phone */}
            <a 
              href={`tel:${cardData.phoneClean}`}
              className="flex items-center gap-4 p-3 rounded-xl bg-[#F9F8F5] hover:bg-[#EBEAE4] transition-colors group"
              data-testid="vcard-phone"
            >
              <div className="w-10 h-10 bg-[#265C39] rounded-full flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-[#6C7D71] uppercase tracking-wide">Phone</p>
                <p className="text-[#1A2E20] font-medium group-hover:text-[#265C39]">{cardData.phone}</p>
              </div>
            </a>

            {/* Email */}
            <a 
              href={`mailto:${cardData.email}`}
              className="flex items-center gap-4 p-3 rounded-xl bg-[#F9F8F5] hover:bg-[#EBEAE4] transition-colors group"
              data-testid="vcard-email"
            >
              <div className="w-10 h-10 bg-[#265C39] rounded-full flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-[#6C7D71] uppercase tracking-wide">Email</p>
                <p className="text-[#1A2E20] font-medium group-hover:text-[#265C39]">{cardData.email}</p>
              </div>
            </a>

            {/* Website */}
            <a 
              href={`https://${cardData.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-3 rounded-xl bg-[#F9F8F5] hover:bg-[#EBEAE4] transition-colors group"
              data-testid="vcard-website"
            >
              <div className="w-10 h-10 bg-[#265C39] rounded-full flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-[#6C7D71] uppercase tracking-wide">Website</p>
                <p className="text-[#1A2E20] font-medium group-hover:text-[#265C39]">{cardData.website}</p>
              </div>
            </a>

            {/* Social Media Row */}
            <div className="flex gap-3 pt-2">
              <a 
                href={`https://facebook.com/${cardData.facebook}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 p-3 rounded-xl bg-[#F9F8F5] hover:bg-[#EBEAE4] transition-colors group"
                data-testid="vcard-facebook"
              >
                <Facebook className="w-5 h-5 text-[#265C39]" />
                <span className="text-sm text-[#1A2E20] font-medium">Facebook</span>
              </a>
              <a 
                href={`https://instagram.com/${cardData.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 p-3 rounded-xl bg-[#F9F8F5] hover:bg-[#EBEAE4] transition-colors group"
                data-testid="vcard-instagram"
              >
                <Instagram className="w-5 h-5 text-[#265C39]" />
                <span className="text-sm text-[#1A2E20] font-medium">Instagram</span>
              </a>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="p-6 pt-2 space-y-3">
            <Button
              onClick={downloadVCard}
              className="w-full bg-[#265C39] hover:bg-[#1E472C] text-white rounded-xl py-6 font-semibold text-lg transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg"
              data-testid="add-to-contacts-button"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              Add to Contacts
            </Button>
            
            <Button
              onClick={shareCard}
              variant="outline"
              className="w-full border-[#265C39] text-[#265C39] hover:bg-[#265C39] hover:text-white rounded-xl py-6 font-semibold text-lg transition-all"
              data-testid="share-card-button"
            >
              <Share2 className="w-5 h-5 mr-2" />
              Share This Card
            </Button>
          </div>

          {/* Footer */}
          <div className="bg-[#265C39] px-6 py-4 text-center">
            <p className="text-[#8CA891] text-xs">
              © {new Date().getFullYear()} New Leaf Independent Housing
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VCardPage;
